// Status de clientes cadastráveis (specs/17-status-clientes.md): busca, cores, badge e select agrupado.
import { GRUPOS_STATUS, STATUS_CLIENTE, type CorStatus, type GrupoStatus } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface StatusCliente {
  id: string; nome: string; cor: CorStatus; grupo: GrupoStatus; ordem: number;
  padraoNovo: boolean; padraoProspeccao: boolean; sistema: boolean; ativo: boolean; clientes: number;
}

/** Fundo e texto de cada cor (tema escuro; contraste do texto ≥ 4.5:1 sobre o fundo). */
export const CORES_UI: Record<CorStatus, { nome: string; bg: string; fg: string }> = {
  cinza: { nome: 'Cinza', bg: '#1f2937', fg: '#d1d5db' },
  azul: { nome: 'Azul', bg: '#15213a', fg: '#93c5fd' },
  verde: { nome: 'Verde', bg: '#13302a', fg: '#6ee7b7' },
  turquesa: { nome: 'Turquesa', bg: '#102b2e', fg: '#5eead4' },
  amarelo: { nome: 'Amarelo', bg: '#3a2e14', fg: '#fde68a' },
  laranja: { nome: 'Laranja', bg: '#3a2214', fg: '#fdba74' },
  vermelho: { nome: 'Vermelho', bg: '#3b1d1d', fg: '#fca5a5' },
  roxo: { nome: 'Roxo', bg: '#1e1b3a', fg: '#c4b5fd' },
};

export const useStatusClientes = () =>
  useQuery({
    queryKey: ['status-clientes'],
    queryFn: () => api<{ data: StatusCliente[] }>('/config/status-clientes').then((r) => r.data),
    staleTime: 60_000,
  });

/** Status ativos agrupados na ordem dos grupos. */
export function porGrupo(lista: StatusCliente[] | undefined) {
  return STATUS_CLIENTE.map((g) => ({
    grupo: g,
    nome: GRUPOS_STATUS[g].nome,
    itens: (lista ?? []).filter((s) => s.ativo && s.grupo === g).sort((a, b) => a.ordem - b.ordem),
  }));
}

export function StatusBadge({ nome, cor }: { nome: string; cor?: string | null }) {
  const c = CORES_UI[(cor ?? 'cinza') as CorStatus] ?? CORES_UI.cinza;
  return (
    <span className="badge status-badge" style={{ background: c.bg, color: c.fg }}>
      <span className="status-dot" style={{ background: c.fg }} aria-hidden="true" />{nome}
    </span>
  );
}

/** Options agrupadas por grupo do sistema. `grupos` inclui “Ativo (todos)” com valor `g:ATIVO`. */
export function OpcoesStatus({ lista, grupos = false }: { lista: StatusCliente[] | undefined; grupos?: boolean }) {
  return (
    <>
      {porGrupo(lista).map((g) => (
        <optgroup key={g.grupo} label={g.nome}>
          {grupos && <option value={`g:${g.grupo}`}>{g.nome} (todos)</option>}
          {g.itens.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </optgroup>
      ))}
    </>
  );
}
